import { useEffect, useState } from 'react'
import type { ReactElement } from 'react'

import type { RouterOutputs } from '../trpc'
import styles from './freshnessBadge.module.scss'

/**
 * The freshness every mirror read carries with it. Taken off the router's own
 * output instead of re-declared, so it cannot drift from what the API sends.
 */
export type Freshness = RouterOutputs['stations']['getGroup']['freshness']

export type FreshnessBadgeProps = {
  /** `undefined` while the query that carries it is still in flight. */
  freshness: Freshness | undefined
}

/**
 * How old the data on screen is, in minutes, from the last CLEAN sync cycle.
 *
 * ⚠️ `lastCleanCycleAt` and not `syncedAt`: `syncedAt` is the newest row the read
 * touched, so with the plant stopped — no actions, no new rows — it goes stale
 * while MARS keeps syncing perfectly. Measuring the lag by the last action seen
 * is exactly what produced false 503s in MARS.
 */
const STALE_AFTER_MINUTES = 5

/**
 * How often the badge re-reads the clock.
 *
 * ⚠️ Without this the warning could never fire where it matters: the lag is
 * computed during render, and a plant screen nobody touches does not re-render.
 * A tablet left on the operations screen would keep saying "hace 3 min" for an
 * hour — the exact state the badge exists to expose. Half the threshold, so a
 * stale mirror is on screen within 30 s of becoming stale.
 */
const TICK_MS = 30_000

const HOUR_MINUTE = new Intl.DateTimeFormat('es-ES', {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
})

const minutesSince = (moment: Date, now: number): number =>
  Math.max(0, Math.floor((now - moment.getTime()) / 60_000))

const lagText = (minutes: number): string =>
  minutes === 0 ? 'hace menos de 1 min' : `hace ${minutes} min`

/**
 * The one indicator every screen showing mirror data must carry: "Datos de las
 * 14:03 (hace 2 min)", in the plant's own words, with a warning style once the
 * last clean cycle is more than five minutes old.
 *
 * The mirror runs up to a couple of minutes behind, and a plant front that
 * pretends to be real time gets an operator to trust a state that is no longer
 * true. So the lag is not hidden and not rounded away: it is shown next to the
 * data it belongs to.
 */
export const FreshnessBadge = ({ freshness }: FreshnessBadgeProps): ReactElement | null => {
  const [now, setNow] = useState(Date.now)

  useEffect(() => {
    const ticker = setInterval(() => setNow(Date.now()), TICK_MS)
    return () => clearInterval(ticker)
  }, [])

  if (freshness === undefined) {
    return null
  }

  const { lastCleanCycleAt } = freshness

  if (lastCleanCycleAt === null) {
    // MARS has never written its cursor, so nothing here can say how old the
    // data is. That is a warning in itself, never a silent absence.
    return (
      <span className={`${styles['freshness-badge']} ${styles['freshness-badge--stale']}`}>
        <span className={styles['freshness-badge__dot']} aria-hidden="true" />
        Sin datos de sincronización
      </span>
    )
  }

  const minutes = minutesSince(lastCleanCycleAt, now)
  const stale = minutes > STALE_AFTER_MINUTES

  return (
    <span
      className={[styles['freshness-badge'], stale ? styles['freshness-badge--stale'] : '']
        .filter(Boolean).join(' ')}
    >
      <span className={styles['freshness-badge__dot']} aria-hidden="true" />
      {`Datos de las ${HOUR_MINUTE.format(lastCleanCycleAt)} (${lagText(minutes)})`}
    </span>
  )
}
